import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldCollection } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { showError, toast } from '@feathr/components';
import type { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useStore } from '@feathr/extender/state';

import type { IDateRangeSection } from '../EmailActivityTab/EmailActivityTab.hooks';
import type { IGroupExportTabHook, ISharedFormSlice } from '../GroupExportModal.types';
import type {
  IOtherActivitiesExportPayload,
  IOtherActivityFieldOption,
  TOtherActivityScope,
} from '../GroupExportModal.utils';
import {
  DEFAULT_OTHER_ACTIVITY_FIELD_IDS,
  getOtherActivityExportColumns,
  getOtherActivityFieldOptions,
  isDuplicateExportError,
} from '../GroupExportModal.utils';

export interface IActivityScopeSection {
  selectedScope: TOtherActivityScope;
  handleChangeScope: (value: TOtherActivityScope) => void;
}

export interface IOtherActivityFieldsSection {
  isCustomFieldsPending: boolean;
  otherActivityFieldOptions: IOtherActivityFieldOption[];
  selectedFieldOptions: IOtherActivityFieldOption[];
  fieldsError: string[] | undefined;
  handleSelectFields: (values: IOtherActivityFieldOption[]) => void;
}

export interface IOtherActivitiesTabState
  extends ISharedFormSlice,
    IDateRangeSection,
    IActivityScopeSection,
    IOtherActivityFieldsSection {}

export interface IUseOtherActivitiesTabOptions {
  form: ISharedFormSlice;
  dateRange: IDateRangeSection;
  resolveDateRange: ReturnType<typeof useExportModal>['resolveDateRange'];
  handleExport: ReturnType<typeof useExportModal>['handleExport'];
  onExportOtherActivities: (payload: IOtherActivitiesExportPayload) => Promise<void>;
  /** Also links the duplicate-export error toast at the in-progress export. */
  successToastAction?: IToastAction;
}

export type IUseOtherActivitiesTabReturn = IGroupExportTabHook<IOtherActivitiesTabState>;

export function useOtherActivitiesTab({
  form,
  dateRange,
  resolveDateRange,
  handleExport,
  onExportOtherActivities,
  successToastAction,
}: IUseOtherActivitiesTabOptions): IUseOtherActivitiesTabReturn {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  const [selectedScope, setSelectedScope] = useState<TOtherActivityScope>('group');
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([
    ...DEFAULT_OTHER_ACTIVITY_FIELD_IDS,
  ]);

  const customFieldsResult = CustomFields.list({
    filters: { collection: FieldCollection.Breadcrumb },
  });

  /*
   * Include defaults while the custom-fields fetch is pending or errored so the
   * preselected chips stay visible. A pending fetch still allows exporting with
   * defaults; an errored fetch surfaces a blocking `fieldsError` below, so the
   * user must close and reopen the modal to retry.
   */
  const otherActivityFieldOptions = useMemo(
    () =>
      getOtherActivityFieldOptions(
        customFieldsResult.isPending || customFieldsResult.isErrored
          ? []
          : customFieldsResult.models,
      ),
    [customFieldsResult.isErrored, customFieldsResult.isPending, customFieldsResult.models],
  );

  const selectedFieldOptions = useMemo(
    () => otherActivityFieldOptions.filter((option) => selectedFieldIds.includes(option.id)),
    [otherActivityFieldOptions, selectedFieldIds],
  );

  const fieldsError = useMemo<string[] | undefined>(() => {
    const errors: string[] = [];
    if (customFieldsResult.isErrored) {
      errors.push(t("Couldn't load custom fields. Close and reopen to retry."));
    }
    if (selectedFieldIds.length === 0) {
      errors.push(t('Choose at least one field to export.'));
    }
    return errors.length > 0 ? errors : undefined;
  }, [customFieldsResult.isErrored, selectedFieldIds.length, t]);

  const handleChangeScope = useCallback((value: TOtherActivityScope): void => {
    setSelectedScope(value);
  }, []);

  const handleSelectFields = useCallback((values: IOtherActivityFieldOption[]): void => {
    setSelectedFieldIds(values.map((v) => v.id));
  }, []);

  const validationErrors = useMemo<string[]>(() => {
    const errors: string[] = [];
    if (form.emailError) {
      errors.push(...form.emailError);
    }
    if (form.exportNameError) {
      errors.push(...form.exportNameError);
    }
    if (dateRange.dateRangeError) {
      errors.push(...dateRange.dateRangeError);
    }
    if (fieldsError) {
      errors.push(...fieldsError);
    }
    return errors;
  }, [form.emailError, form.exportNameError, dateRange.dateRangeError, fieldsError]);

  const handleExportError = useCallback(
    (error: unknown): void => {
      if (isDuplicateExportError(error)) {
        toast(
          {
            title: t('An identical export is already in progress.'),
            description: t('Wait for it to finish before exporting this group again.'),
            ...(successToastAction ? { action: successToastAction } : {}),
          },
          { type: 'error' },
        );
        return;
      }
      showError(error, t);
    },
    [successToastAction, t],
  );

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const { startDate, endDate } = resolveDateRange();
    const payload: IOtherActivitiesExportPayload = {
      ...getOtherActivityExportColumns(selectedFieldIds, otherActivityFieldOptions),
      scope: selectedScope,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      email: form.email,
      name: form.exportName,
    };
    let didSucceed = false;
    await handleExport(
      () => onExportOtherActivities(payload),
      () => {
        didSucceed = true;
      },
      t,
      {
        successMessage: t('Your other activities export has started.'),
        successToastAction,
        onError: handleExportError,
      },
    );
    return didSucceed;
  }, [
    form.email,
    form.exportName,
    handleExport,
    handleExportError,
    onExportOtherActivities,
    otherActivityFieldOptions,
    resolveDateRange,
    selectedFieldIds,
    selectedScope,
    successToastAction,
    t,
  ]);

  const resetState = useCallback((): void => {
    setSelectedScope('group');
    setSelectedFieldIds([...DEFAULT_OTHER_ACTIVITY_FIELD_IDS]);
  }, []);

  return {
    state: {
      ...form,
      ...dateRange,
      selectedScope,
      handleChangeScope,
      isCustomFieldsPending: customFieldsResult.isPending,
      otherActivityFieldOptions,
      selectedFieldOptions,
      fieldsError,
      handleSelectFields,
    },
    validationErrors,
    handleSubmit,
    resetState,
  };
}
